import React, { useMemo, useState } from 'react';

import { Autocomplete, CircularProgress, TextField } from '@mui/material';
import type { SxProps, Theme } from '@mui/material/styles';

import { SEARCH_DEBOUNCE_MS } from '~/consts/tours';

import { useSearchTourOffers } from '~/hooks/apis/tours/useTourOffers';
import useDebounced from '~/hooks/useDebounced';

import { TourOfferSummary } from '~/types/tours/tourOffer';

interface Props {
  value: TourOfferSummary | null;
  onChange: (offer: TourOfferSummary | null) => void;
  label?: string;
  sx?: SxProps<Theme>;
}

export default function BaseTourPicker({ value, onChange, label = 'Base tour', sx }: Props) {
  const [inputValue, setInputValue] = useState('');

  const search = useDebounced(inputValue.trim(), SEARCH_DEBOUNCE_MS);

  const { data: results = [], isFetching } = useSearchTourOffers(search, false);

  // The bundle's LTLE is made from a base tour, so existing LTLEs are not offered.
  const options = useMemo(() => results.filter((offer) => !offer.isLtle), [results]);

  // Kept in the list so the field does not clear itself as the search changes.
  const mergedOptions = useMemo(
    () => (!value || options.some((option) => option.id === value.id) ? options : [value, ...options]),
    [options, value],
  );

  return (
    <Autocomplete
      sx={sx}
      value={value}
      onChange={(_event, offer) => onChange(offer)}
      inputValue={inputValue}
      onInputChange={(_event, next) => setInputValue(next)}
      options={mergedOptions}
      getOptionLabel={(option) => option.name ?? option.id}
      isOptionEqualToValue={(option, selected) => option.id === selected.id}
      filterOptions={(all) => all}
      loading={isFetching}
      noOptionsText={search ? `No tours match "${search}"` : 'No tours found'}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          helperText="Search by name, tour offer id or legacy tour id"
          required
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {isFetching ? <CircularProgress size={16} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
}
